import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Plus } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SERVICES } from "@/data/services";

const serviceTitle = (t) =>
  t
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bSeo\b/g, "SEO")
    .replace(/\bAi\b/g, "AI")
    .replace(/\bUgc\b/g, "UGC");

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const index = SERVICES.findIndex((s) => s.slug === slug);
  const service = index !== -1 ? SERVICES[index] : null;

  const faqSchema =
    service && service.faqs && service.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: service.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : undefined;

  usePageMeta({
    title: service ? `${serviceTitle(service.title)} | BrandZenith` : "Services | BrandZenith",
    description: service ? service.desc : "BrandZenith services.",
    path: `/services/${slug}`,
    image: service ? service.img : undefined,
    schema: faqSchema,
  });

  if (!service) return <Navigate to="/services" replace />;

  const next = SERVICES[(index + 1) % SERVICES.length];
  const words = service.title.split(" ");
  const accent = words[words.length - 1];
  const title = words.slice(0, -1).join(" ");

  return (
    <>
      <Navbar />
      <main data-testid="service-detail-page">
        <PageHeader
          overline={`Service ${service.n} — ${service.tags}`}
          title={title}
          accent={accent}
          copy={service.desc}
        />

        <section className="relative pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div
                data-testid="service-artwork"
                className="relative aspect-[16/9] sm:aspect-[21/9] rounded-lg overflow-hidden border border-white/[0.08] mb-16 lg:mb-24"
              >
                <img
                  src={service.img}
                  alt={`${service.title} — cinematic artwork by BrandZenith`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-t from-[#050507]/80 via-transparent to-[#050507]/20"
                />
                <span
                  aria-hidden="true"
                  className="absolute bottom-4 right-6 font-display text-[18vw] sm:text-[10rem] font-extrabold leading-none text-white/[0.06] select-none"
                >
                  {service.n}
                </span>
              </div>
            </Reveal>

            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-14 lg:gap-20">
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
                  The Approach
                </p>
                <p className="text-lg sm:text-2xl font-display font-semibold tracking-tight leading-snug text-white/90">
                  {service.long}
                </p>
                <div className="mt-10 border-l-2 border-[#6D00FF] pl-6">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#A6A6B0]/70 mb-2">
                    The Outcome
                  </p>
                  <p className="font-display text-lg sm:text-xl font-bold tracking-tight text-gradient-purple">
                    {service.outcome}
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="glass-card rounded-lg p-8">
                  <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
                    What's Included
                  </p>
                  <ul className="space-y-1">
                    {service.deliverables.map((d, i) => (
                      <li
                        key={d}
                        data-testid={`service-deliverable-${i}`}
                        className="group flex items-center gap-4 py-3.5 border-b border-white/[0.06] last:border-b-0"
                      >
                        <span className="font-display text-[11px] font-bold tracking-[0.18em] text-[#8B2CFF]/70 group-hover:text-[#C43CFF] transition-colors duration-300">
                          0{i + 1}
                        </span>
                        <span className="text-sm text-white/85">{d}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid="service-detail-cta"
                    className="mt-8 flex items-center justify-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-xs font-bold uppercase tracking-[0.16em] px-6 py-3.5 rounded-md transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_32px_-8px_rgba(139,44,255,0.8)]"
                  >
                    Start With This Service <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>
            </div>

            <div className="mt-24 lg:mt-32">
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-10">
                  How It Moves
                </p>
              </Reveal>
              <div className="grid sm:grid-cols-3 gap-5">
                {service.steps.map((step, i) => (
                  <Reveal key={step.t} delay={i * 0.12} className="h-full">
                    <div
                      data-testid={`service-step-${i}`}
                      className="h-full glass-card rounded-lg p-7 transition-[border-color,box-shadow] duration-500 hover:border-[#6D00FF]/40 hover:shadow-[0_0_36px_-12px_rgba(109,0,255,0.5)]"
                    >
                      <span className="font-display text-3xl font-extrabold tracking-tighter text-gradient-purple">
                        0{i + 1}
                      </span>
                      <h3 className="font-display text-base font-bold uppercase tracking-[0.1em] mt-4 mb-3">
                        {step.t}
                      </h3>
                      <p className="text-sm text-[#A6A6B0] leading-relaxed">{step.d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div className="mt-24 lg:mt-32 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
                  Built For
                </p>
                <h2 className="font-display text-2xl sm:text-4xl font-extrabold uppercase tracking-tight leading-[1.1]">
                  This service is for you <span className="text-gradient-purple">if…</span>
                </h2>
              </Reveal>
              <div>
                {service.fit.map((f, i) => (
                  <Reveal key={f} delay={i * 0.08} y={20}>
                    <div
                      data-testid={`service-fit-${i}`}
                      className="flex items-start gap-4 py-5 border-b border-white/[0.06] first:border-t"
                    >
                      <Plus className="w-4 h-4 mt-0.5 shrink-0 text-[#C43CFF]" strokeWidth={2} />
                      <p className="text-sm sm:text-base text-[#A6A6B0] leading-relaxed">{f}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {service.faqs && service.faqs.length > 0 && (
              <div className="mt-24 lg:mt-32 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                <Reveal>
                  <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
                    Questions, Answered
                  </p>
                  <h2 className="font-display text-2xl sm:text-4xl font-extrabold uppercase tracking-tight leading-[1.1]">
                    Before you <span className="text-gradient-purple">ask…</span>
                  </h2>
                  <p className="mt-5 text-sm sm:text-base text-[#A6A6B0] leading-relaxed max-w-md">
                    The questions every client asks before starting this service — answered
                    honestly, without the sales gloss.
                  </p>
                </Reveal>
                <Reveal delay={0.1}>
                  <Accordion type="single" collapsible data-testid="service-faq" className="w-full">
                    {service.faqs.map((f, i) => (
                      <AccordionItem
                        key={f.q}
                        value={`faq-${i}`}
                        className="border-white/[0.08]"
                      >
                        <AccordionTrigger
                          data-testid={`service-faq-question-${i}`}
                          className="font-display text-sm sm:text-base font-bold tracking-tight text-white/90 hover:text-white hover:no-underline py-5 [&>svg]:text-[#8B2CFF]"
                        >
                          {f.q}
                        </AccordionTrigger>
                        <AccordionContent
                          data-testid={`service-faq-answer-${i}`}
                          className="text-sm text-[#A6A6B0] leading-relaxed"
                        >
                          {f.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Reveal>
              </div>
            )}

            <div className="mt-20 flex flex-wrap items-center justify-between gap-6 border-t border-white/[0.06] pt-8">
              <Link
                to="/services"
                data-testid="service-back-link"
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#A6A6B0] hover:text-white transition-colors duration-300"
              >
                <ArrowLeft className="w-4 h-4" /> All Services
              </Link>
              <Link
                to={`/services/${next.slug}`}
                data-testid="service-next-link"
                className="group text-right"
              >
                <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#A6A6B0]/60 mb-1">
                  Next Service
                </span>
                <span className="font-display text-lg sm:text-xl font-extrabold uppercase tracking-tight text-white/80 group-hover:text-gradient-purple transition-colors duration-300 flex items-center gap-3">
                  {next.title} <ArrowRight className="w-5 h-5 text-[#8B2CFF] transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}
